import { useRef, type ComponentType, type KeyboardEvent, type ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { Icon } from '../Icon';

export type SegmentOption<V extends string> = {
  value: V;
  label: ReactNode;
  icon?: ComponentType<IconProps>;
  disabled?: boolean;
  /** Extra classes on this option's button, e.g. to hide it by breakpoint before JS can decide. */
  className?: string;
};

export type SegmentedControlProps<V extends string> = {
  options: Array<SegmentOption<V>>;
  value: V;
  onValueChange: (value: V) => void;
  /** Names the group for screen readers ("View", "Pay shown as"), like every other component's label. */
  label: string;
  size?: 'sm' | 'md';
  /** Below the sm breakpoint, options with an icon show only the icon (the label stays for screen
   * readers), so a second control fits beside another on one phone row. */
  iconOnlyOnPhones?: boolean;
  className?: string;
};

/**
 * Two to five mutually exclusive options in a pill track; the chosen one fills with the selected
 * colour. A radio group under the hood: one tab stop, arrow keys move and select, Home/End jump.
 * More than five options: use Select.
 */
export function SegmentedControl<V extends string>({
  options,
  value,
  onValueChange,
  size = 'md',
  iconOnlyOnPhones = false,
  className,
  label,
}: SegmentedControlProps<V>) {
  devWarn(
    options.length < 2 || options.length > 5,
    `SegmentedControl has ${options.length} options; use 2 to 5 (more: a Select).`,
  );
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const current = options.findIndex((o) => o.value === value);

  const move = (to: number) => {
    const option = options[to];
    if (!option) return;
    onValueChange(option.value);
    refs.current[to]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const at = enabled.indexOf(current);
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (step) {
      e.preventDefault();
      move(enabled[(at + step + enabled.length) % enabled.length]);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move(enabled[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      move(enabled[enabled.length - 1]);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('inline-flex rounded-jc-control bg-jc-surface ring-1 ring-inset ring-jc-border-control', className)}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={o.disabled}
            tabIndex={selected || (current < 0 && i === enabled[0]) ? 0 : -1}
            onClick={() => onValueChange(o.value)}
            onKeyDown={onKeyDown}
            className={cx(
              'inline-flex items-center justify-center gap-1.5 rounded-jc-control text-sm leading-none whitespace-nowrap',
              // 12px a side on phones, so two controls fit one row at 375px; 16px from sm.
              'px-3 sm:px-4',
              'transition-colors duration-(--jc-duration-fast) ease-jc-out motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-1 focus-visible:ring-offset-jc-surface',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'h-10',
              selected
                ? 'bg-jc-selected font-semibold text-jc-on-selected'
                : 'font-semibold text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink',
              o.className,
            )}
          >
            {o.icon ? <Icon icon={o.icon} size="sm" /> : null}
            {iconOnlyOnPhones && o.icon ? <span className="max-sm:sr-only">{o.label}</span> : o.label}
          </button>
        );
      })}
    </div>
  );
}
